import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { AppWrapper } from "./components/common/PageMeta.tsx";
import { ThemeProvider } from "./context/ThemeContext.tsx";
import { queryClient } from "./lib/queryClient.ts";
import { reloadOnceForNewBuild } from "./lib/lazyWithRetry.ts";
import { maskSentryBreadcrumb, maskSentryEvent } from "../shared/secret-path";

// 페이지 청크의 404 는 lazyWithRetry 가 받는다. 그런데 **여러 페이지가 나눠 쓰는 공용
// 청크**는 페이지 청크보다 먼저 받아지므로, 그 단계에서 404 가 나면 lazyWithRetry 까지
// 도달하지도 못한다(관세사에서 실제로 그 경우가 관측됐다 — Sentry 3건 중 1건).
// vite 가 그 실패를 `vite:preloadError` 로 따로 알려 주므로 여기서 같은 처방을 한다 —
// 조용히 딱 한 번 새로고침. 새로고침을 시작했으면 원래 에러는 던지지 않는다.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnceForNewBuild()) event.preventDefault();
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  // 운영에서만 SDK 가동 — dev 이벤트로 무료 플랜 한도(월 5,000)를 갉지 않도록.
  // 운영용 빌드라도 개발 PC(localhost)에서 띄운 것은 끈다 — 헤드리스 자동검증이 운영 이슈로
  // 섞여 들어가 «시세표 미반영» 경보 10건이 가짜로 쌓였다(2026-09-29).
  enabled:
    import.meta.env.PROD &&
    !!import.meta.env.VITE_SENTRY_DSN &&
    !/^(localhost|127\.|0\.0\.0\.0|\[?::1\]?)/.test(window.location.hostname),
  // 성능 트레이스 끔 (2026-09-26) — 핸드폰·관세사·드림이 같은 Sentry 조직(nexport)의
  // 월 span 한도(500만)를 나눠 쓰다 80%에 닿아 사용자 결정으로 일단 끈다. 에러 수집은 무관.
  // 다시 켤 땐 전수(1.0)가 아니라 필요한 경로만 tracesSampler 로 고른다(핸드폰 커밋 14de5065 참고).
  tracesSampleRate: 0,
  // 세션 리플레이는 쓰지 않으므로(샘플레이트 0) replayIntegration 자체를 빼 번들에서 제거.
  // → Sentry 청크가 크게 줄어든다. 다시 켜려면 replayIntegration + replays*SampleRate 복구.
  integrations: [Sentry.browserTracingIntegration()],
  // 🔒 공개 창구 주소(/form/<토큰>·/talk/<토큰> …)의 토큰과 질의문자열을 원문으로 보내지 않는다 —
  //    이벤트 url·화면 이름·이동/요청 기록(breadcrumb) 모두 경로 틀(/form/:token)로 바꾼다(shared/secret-path.ts, 2026-10-06).
  beforeSend: (event) => maskSentryEvent(event),
  beforeBreadcrumb: (breadcrumb) => maskSentryBreadcrumb(breadcrumb),
  sendDefaultPii: false,
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppWrapper>
          <App />
        </AppWrapper>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);
